import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { XR_PALETTE } from "./xrAssets";

const round = (g, x, y, w, h, r) => { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); };
const wrap = (g, text, x, y, max, lh) => {
  let line = "";
  text.split(" ").forEach((w) => { const t = line ? `${line} ${w}` : w; if (g.measureText(t).width > max && line) { g.fillText(line, x, y); line = w; y += lh; } else line = t; });
  if (line) g.fillText(line, x, y);
};

const useCanvasTex = (w, h, draw, deps) => {
  const { c, tex } = useMemo(() => { const c = document.createElement("canvas"); c.width = w; c.height = h; const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; return { c, tex }; }, [w, h]);
  useEffect(() => { const g = c.getContext("2d"); g.clearRect(0, 0, w, h); draw(g); tex.needsUpdate = true; }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return tex;
};

/** World-locked navy/teal/gold glass panel (canvas text, no font atlas) — readable in the headset at ~1.6 m. */
export const Panel = ({ title, body, kicker, position, width = 1.1, button, onButton, testId }) => {
  const tex = useCanvasTex(1024, 560, (g) => {
    round(g, 8, 8, 1008, 544, 56); g.fillStyle = "rgba(10,37,64,0.92)"; g.fill();
    g.lineWidth = 6; g.strokeStyle = XR_PALETTE.teal; g.stroke();
    g.fillStyle = XR_PALETTE.teal; g.font = "800 30px Manrope, sans-serif"; g.fillText((kicker || "").toUpperCase(), 64, 88);
    g.fillStyle = "#FFE29A"; g.font = "600 64px Fraunces, Georgia, serif"; g.fillText(title, 64, 170);
    g.fillStyle = "#F4F8FB"; g.font = "500 38px Manrope, sans-serif"; wrap(g, body, 64, 245, 900, 54);
  }, [title, body, kicker]);
  const btn = useCanvasTex(512, 128, (g) => {
    round(g, 4, 4, 504, 120, 60); g.fillStyle = XR_PALETTE.gold; g.fill();
    g.fillStyle = XR_PALETTE.navy; g.font = "800 46px Manrope, sans-serif"; g.textAlign = "center"; g.fillText(button || "", 256, 80);
  }, [button]);
  const hover = useRef(false), bm = useRef();
  useFrame(() => { if (bm.current) bm.current.scale.setScalar(THREE.MathUtils.lerp(bm.current.scale.x, hover.current ? 1.08 : 1, 0.2)); });
  return (
    <group position={position} name={testId}>
      <mesh><planeGeometry args={[width, width * 0.547]} /><meshBasicMaterial map={tex} transparent toneMapped={false} /></mesh>
      {button && (
        <mesh ref={bm} position={[0, -width * 0.36, 0.01]} name={`${testId}-button`} onClick={(e) => { e.stopPropagation(); onButton?.(); }}
          onPointerOver={() => (hover.current = true)} onPointerOut={() => (hover.current = false)}>
          <planeGeometry args={[width * 0.42, width * 0.105]} /><meshBasicMaterial map={btn} transparent toneMapped={false} />
        </mesh>
      )}
    </group>
  );
};

/** In-headset FPS meter (therapist toggle): average over 0.5 s, green ≥72, gold ≥60, red below. */
export const FpsMeter = ({ position = [-0.62, 0.92, -1.25], onSample }) => {
  const acc = useRef({ n: 0, t: 0, fps: 0 }), c = useMemo(() => document.createElement("canvas"), []);
  const tex = useMemo(() => { c.width = 256; c.height = 96; const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t; }, [c]);
  useFrame((_, dt) => {
    const a = acc.current; a.n += 1; a.t += dt;
    if (a.t < 0.5) return;
    a.fps = Math.round(a.n / a.t); a.n = 0; a.t = 0; onSample?.(a.fps);
    const g = c.getContext("2d"); g.clearRect(0, 0, 256, 96);
    round(g, 2, 2, 252, 92, 30); g.fillStyle = "rgba(10,37,64,0.85)"; g.fill();
    g.fillStyle = a.fps >= 72 ? "#5BE3B4" : a.fps >= 60 ? "#FFE29A" : "#FF8A80"; g.font = "800 48px Manrope, sans-serif"; g.fillText(`${a.fps} FPS`, 28, 64);
    tex.needsUpdate = true;
  });
  return <mesh position={position} rotation-y={0.35} name="xr-fps-meter"><planeGeometry args={[0.22, 0.0825]} /><meshBasicMaterial map={tex} transparent toneMapped={false} depthTest={false} /></mesh>;
};
